import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { RosterArtistCard } from '../components/RosterArtistCard';
import { useContent } from '../context/ContentContext';
import { usePageMeta } from '../hooks/usePageMeta';

const OlahBlissCard = ({ element45 }) => (
  <div
    className="bg-silver-900/20 border border-silver-400/20 rounded-2xl overflow-hidden mb-16 group"
    data-testid="artist-card-olah-bliss"
  >
    <div className="grid md:grid-cols-3 items-center">
      <div className="relative h-64 md:h-72">
        <img
          src={element45.image}
          alt={element45.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="md:col-span-2 p-8 md:p-10">
        <p className="text-silver-400 text-xs uppercase tracking-[0.25em] mb-2">Element 45</p>
        <h3 className="text-white text-2xl md:text-3xl font-black mb-2">{element45.name}</h3>
        <p className="text-silver-400 text-sm uppercase tracking-wider mb-4">{element45.genre}</p>
        <p className="text-white/70 text-base leading-relaxed mb-6 line-clamp-3">{element45.bio}</p>
        <Link
          to="/element45/olah-bliss"
          className="inline-flex items-center gap-2 text-silver-400 hover:text-silver-300 font-bold text-sm uppercase tracking-wider transition-colors"
          data-testid="olah-bliss-page-link"
        >
          View Artist Page
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  </div>
);

const DemoCTA = () => (
  <div className="bg-silver-900/20 border border-silver-400/20 rounded-xl p-8 text-center max-w-3xl mx-auto">
    <p className="text-silver-400 text-sm md:text-base mb-4">Are you an artist who moves different?</p>
    <a
      href="mailto:info@rhodiumrecords.music"
      className="inline-flex items-center gap-2 px-8 py-4 bg-silver-400 hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-wider rounded-lg transition-all duration-300 hover:scale-105"
      data-testid="submit-demo-btn"
    >
      Submit Your Demo
    </a>
    <p className="text-white/40 text-sm mt-8 italic">Rare by nature, relentless by design</p>
  </div>
);

const RosterPageHeader = () => (
  <div className="max-w-4xl mb-16">
    <p className="text-silver-400 text-sm uppercase tracking-[0.3em] mb-4">The Roster</p>
    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black">
      <span className="text-white">Rhodium </span>
      <span className="text-silver-400">Artists</span>
    </h1>
    <p className="text-white/60 text-base md:text-lg mt-4 max-w-2xl">
      Rare by nature, relentless by design. Meet the artists who bend the atmosphere without asking permission.
    </p>
  </div>
);

const Artists = ({ setCurrentTrack }) => {
  usePageMeta(
    'Artists | Rhodium Records — Òlah Bliss, Lets Get It Boy Lil D, Yellow Zoo',
    'Meet the Rhodium Records roster: Òlah Bliss, Detroit rapper Lets Get It Boy Lil D, and Brooklyn Pop/Hip-Hop artist Yellow Zoo.'
  );
  const location = useLocation();
  const { roster, element45 } = useContent();

  const playTrack = (artist, track) => {
    if (setCurrentTrack) {
      setCurrentTrack({
        title: track.title,
        artist: artist.name,
        artwork: artist.image,
        spotifyUrl: track.spotifyUrl
      });
    }
  };

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-black pt-52 pb-32" data-testid="artists-page">
      <div className="container mx-auto px-6">
        <RosterPageHeader />

        {roster.map((artist) => (
          <RosterArtistCard key={artist.slug} artist={artist} onPlayTrack={playTrack} />
        ))}

        {element45 && <OlahBlissCard element45={element45} />}

        <DemoCTA />
      </div>
    </div>
  );
};

export default Artists;
